import {useEffect, useMemo, useState} from 'react';

import {faGlass} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import BeerPriceField from 'features/beersManager/BeerPriceField/BeerPriceField';
import {editPubBeerACS} from 'features/beersManager/requestAC';
import {setCheckedBeerAC} from 'features/beersManager/slice';
import {useBeerPrices} from 'hooks/useBeerPrices';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import {setModalAC} from 'store/modal/modalSlice';
import {EditPubBeerDTO} from 'typescript/dto/editPubBeerDTO';
import {IBeerPriceVariant} from 'typescript/interfaces/BeerPriceVariant';
import '../CheckedBeerInfo/CheckedBeerInfo.scss';

const EditBeer = () => {
  const dispatch = useSetGlobalState();

  const {checkedBeer, pubBeers} = useGlobalState((state) => state.beersManager);
  const matchingBeer = checkedBeer && pubBeers.find((pubBeer) => pubBeer.id === String(checkedBeer.id));

  const [prices, setBeerPrices, handleUpdatePrice, handleRemovePrice] = useBeerPrices();
  const [shouldResetState, setShouldResetState] = useState(false);

  const enteredPrice = useMemo(() => prices.find((price) => price.amount && price.price), [prices]);

  const handleCancel = () => {
    setShouldResetState(true);
    dispatch(setModalAC(null));
  };

  const handleEdit = () => {
    if (!matchingBeer) return;

    const priceVariants: IBeerPriceVariant[] = [];

    prices.forEach((p) => {
      const destroyVariant = p.destroy;
      const updateVariant = p.existingPrice && !p.destroy;
      const createNewVariant = !p.existingPrice && p.price && p.amount;

      if (destroyVariant) {
        const deletedVariant: IBeerPriceVariant = {
          id: p.id,
          _destroy: true,
          currency: 0,
          price: 0,
          amount: 0,
        };

        priceVariants.push(deletedVariant);
      }

      if (updateVariant) {
        const updatedVariant: IBeerPriceVariant = {
          amount: +p.amount,
          price: +p.price,
          currency: +p.currency,
          id: p.id,
        };

        priceVariants.push(updatedVariant);
      }

      if (createNewVariant) {
        const newVariant: IBeerPriceVariant = {
          price: +p.price,
          amount: +p.amount,
          currency: +p.currency,
        };

        priceVariants.push(newVariant);
      }
    });

    const dto: EditPubBeerDTO = {
      pub_beer_id: +matchingBeer.id,
      beer_variants_attributes: priceVariants,
    };

    dispatch(editPubBeerACS(dto));
    handleCancel();
  };

  useEffect(() => {
    return () => {
      if (shouldResetState) {
        dispatch(setCheckedBeerAC(null));
        setShouldResetState(false);
      }
    };
    // eslint-disable-next-line
  }, [shouldResetState]);

  useEffect(() => {
    if (matchingBeer?.attributes.beer_variants) {
      setBeerPrices(matchingBeer?.attributes.beer_variants);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="checked-beer-info">
      <div className="checked-beer-info__header">
        <div className="checked-beer-info__image">
          {!checkedBeer?.imageUrl ? (
            <div className="checked-beer-info__placeholder">
              <FontAwesomeIcon icon={faGlass} />
            </div>
          ) : (
            <img src={checkedBeer?.imageUrl} alt={`${checkedBeer.name} beer`} />
          )}
        </div>
        <Text tag="h3">
          Edit <span>{checkedBeer?.name}</span> beer
        </Text>
      </div>
      <div className="checked-beer-info__fields">
        {prices.map(({id, price, currency, amount}) => (
          <BeerPriceField
            key={id}
            id={id}
            price={price}
            currency={currency}
            amount={amount}
            handleUpdatePrice={handleUpdatePrice}
            handleRemovePrice={handleRemovePrice}
          />
        ))}
      </div>
      <div className="checked-beer-info__actions checked-beer-info__actions--edit">
        <Button variant="tertiary-outlined" className="checked-beer-info__remove" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="tertiary" disabled={!enteredPrice} className="checked-beer-info__save" onClick={handleEdit}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default EditBeer;
